import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Box,
  Alert,
  Tooltip,
} from "@mui/material";
import { FaSignOutAlt, FaShieldAlt } from "react-icons/fa";
import ConfirmDialog from "../../components/ConfirmDialog";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../context/AuthContext";
import Stack from "@mui/material/Stack";

function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  const { user: currentUser } = useAuth();

  const fetchUsers = async () => {
    try {
      const res = await axiosInstance.get("/users");
      setUsers(res.data);
    } catch (err) {
      setError("Failed to load users list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleLogoutClick = (user) => {
    setSelectedUser(user);
    setOpenLogoutConfirm(true);
  };

  const handleForceLogout = async () => {
    try {
      await axiosInstance.post(`/users/force-logout/${selectedUser._id}`);
      toast.success(`Forcefully logged out ${selectedUser.fullName || selectedUser.userId}`);
      setOpenLogoutConfirm(false);
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to forcefully logout user");
    }
  };

  const renderRoleBadge = (roleName, roleId) => {
    let bg = "rgba(100, 116, 139, 0.08)";
    let border = "rgba(100, 116, 139, 0.15)";
    let color = "#475569";

    if (roleId === 1) {
      bg = "rgba(239, 68, 68, 0.08)";
      border = "rgba(239, 68, 68, 0.15)";
      color = "#dc2626";
    } else if (roleId === 2) {
      bg = "rgba(99, 102, 241, 0.08)";
      border = "rgba(99, 102, 241, 0.15)";
      color = "#4f46e5";
    } else if (roleId === 3) {
      bg = "rgba(16, 185, 129, 0.08)";
      border = "rgba(16, 185, 129, 0.15)";
      color = "#059669";
    }

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          px: 1.5,
          py: 0.25,
          borderRadius: "12px",
          backgroundColor: bg,
          border: "1px solid",
          borderColor: border,
          color: color,
          fontSize: "0.75rem",
          fontWeight: 600,
          textTransform: "capitalize",
        }}
      >
        {roleName || (roleId === 1 ? "Super Admin" : roleId === 2 ? "Admin" : "Member")}
      </Box>
    );
  };

  const getRowStyles = (roleId) => {
    let leftBorderColor = "#64748b";
    let bgColor = "rgba(100, 116, 139, 0.005)";
    let borderColor = "rgba(100, 116, 139, 0.08)";
    let hoverBg = "rgba(100, 116, 139, 0.015) !important";
    let hoverBorderColor = "rgba(100, 116, 139, 0.2)";

    if (roleId === 1) {
      leftBorderColor = "#ef4444";
      bgColor = "rgba(239, 68, 68, 0.005)";
      borderColor = "rgba(239, 68, 68, 0.06)";
      hoverBg = "rgba(239, 68, 68, 0.015) !important";
      hoverBorderColor = "rgba(239, 68, 68, 0.18)";
    } else if (roleId === 2) {
      leftBorderColor = "#4f46e5";
      bgColor = "rgba(99, 102, 241, 0.01)";
      borderColor = "rgba(99, 102, 241, 0.06)";
      hoverBg = "rgba(99, 102, 241, 0.025) !important";
      hoverBorderColor = "rgba(99, 102, 241, 0.18)";
    } else if (roleId === 3) {
      leftBorderColor = "#10b981";
      bgColor = "rgba(16, 185, 129, 0.01)";
      borderColor = "rgba(16, 185, 129, 0.06)";
      hoverBg = "rgba(16, 185, 129, 0.025) !important";
      hoverBorderColor = "rgba(16, 185, 129, 0.18)";
    }

    return {
      backgroundColor: bgColor,
      borderLeft: `4px solid ${leftBorderColor}`,
      borderRadius: "4px",
      mb: 1,
      transition: "all 0.2s ease-in-out",
      boxShadow: `0 1px 3px ${borderColor}`,
      "& td": {
        borderBottom: "1px solid",
        borderColor: borderColor,
      },
      "&:hover": {
        backgroundColor: hoverBg,
        boxShadow: `0 2px 8px ${hoverBorderColor}`,
      },
    };
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={3}
      >
        <Typography variant="h5" fontWeight="bold">
          User Sessions & Authorization
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <ConfirmDialog
        open={openLogoutConfirm}
        onClose={() => setOpenLogoutConfirm(false)}
        onConfirm={handleForceLogout}
        message={`Are you sure you want to forcefully logout ${selectedUser?.fullName}? This will terminate all active sessions for this user.`}
      />

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none", backgroundColor: "transparent" }}>
        <TableContainer>
          <Table sx={{ borderCollapse: "separate", borderSpacing: "0 8px", minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ "& th": { border: "none", backgroundColor: "transparent", color: "text.secondary", py: 1 } }}>
                <TableCell><b>User ID</b></TableCell>
                <TableCell><b>Name</b></TableCell>
                <TableCell><b>Mobile</b></TableCell>
                <TableCell><b>Prefix / Tenant</b></TableCell>
                <TableCell><b>Employer</b></TableCell>
                <TableCell><b>Role</b></TableCell>
                <TableCell><b>Session Version</b></TableCell>
                <TableCell align="center"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {users.map((item) => {
                const isMe = String(currentUser?._id) === String(item._id);
                return (
                  <TableRow key={item._id} sx={getRowStyles(item.roleId)}>
                    <TableCell sx={{ py: 1.5, fontWeight: 600, color: "text.secondary" }}>
                      {item.userId}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 600, color: "text.primary" }}>
                      {item.fullName} {isMe && " (You)"}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {item.mobile}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {item.prefix || "—"}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {item.employerName || (item.roleId === 1 ? "System (Super Admin)" : "—")}
                    </TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      {renderRoleBadge(item.roleName, item.roleId)}
                    </TableCell>
                    <TableCell sx={{ py: 1.5, fontWeight: 600 }}>
                      v{item.tokenVersion || 0}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Tooltip title={isMe ? "You cannot log out yourself" : "Force Logout User"}>
                        <span>
                          <IconButton
                            color="error"
                            disabled={isMe}
                            onClick={() => handleLogoutClick(item)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "rgba(239, 68, 68, 0.08)",
                              },
                            }}
                          >
                            <FaSignOutAlt />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default UsersList;
